import Arbeitsplatz from '@/types/Arbeitsplatz';
import Beleg from '@/types/Beleg';
import BelegPatient from '@/types/BelegPatienten';
import Belegposition from '@/types/Belegposition';
import Briefart from '@/types/Briefart';
import Charge from '@/types/Charge';
import ElgaDosis from '@/types/ElgaDosis';
import ElgaPatient from '@/types/soap/elga/Patient';
import Elgaimpfstoffe from '@/types/ElgaImpfstoffe';
import ImmunizationTarget from '@/types/ImmunizationTarget';
import ImpfDbImpfstoff from '@/types/ImpfDbImpfstoff';
import ImpfDbLog from '@/types/ImpfDbLog';
import Impfdosis from '@/types/Impfdosis';
import Impfempfehlung from '@/types/Impfempfehlung';
import Impferinnerung from '@/types/Impferinnerung';
import Impferinnerungstext from '@/types/Impferinnerungstext';
import Impfschema from '@/types/Impfschema';
import Impfstelle from '@/types/Impfstelle';
import Anforderung from '@/types/Anforderung';
import Impfstoff from '@/types/Impfstoffe';
import Impfung from '@/types/Impfung';
import Interval from '@/helper/Interval';
import Karteieintrag from '@/types/Karteieintrag';
import Kassaabschluss from '@/types/Kassaabschluss';
import Lagerbuchung from '@/types/Lagerbuchung';
import Lagerbuchungsart from '@/types/Lagerbuchungsart';
import Land from '@/types/Land';
import Leistung from '@/types/Leistung';
import OffeneVerrechnung from '@/types/OffeneVerrechnung';
import PatUntersuchung from '@/types/PatUntersuchung';
import Patient from '@/types/Patient';
import Person from '@/types/Person';
import Planimpfung from '@/types/Planimpfung';
import Postleitzahl from '@/types/Postleitzahl';
import Reiseart from '@/types/Reiseart';
import Reiseplan, { PlanItem } from '@/types/Reiseplan';
import Reiseziel from '@/types/Reiseziel';
import Rezept from '@/types/Rezept';
import SpecialSituationIndication from '@/types/SpecialSituationIndication';
import Systemdaten from '@/types/Systemdaten';
import Teilimpfungen from '@/types/Teilimpfungen';
import Terminart from '@/types/Terminart';
import Text from '@/types/Text';
import Titel from '@/types/Titel';
import Untersuchung from '@/types/Untersuchung';
import Untersuchungsbefundvariante from '@/types/Untersuchungsbefundvariante';
import User, { UserRoleType } from '@/types/User';
import Versicherung from '@/types/Versicherung';
import Warteliste from '@/types/Warteliste';
import Zahlungsart from '@/types/Zahlungsart';
import { Card, CardInfo } from '@/types/soap/elga/Card';
import { CompareFunction, GridParams } from '@/types';
import { Eintragsart, Methods, Priority } from '@/types/Logbuch';
import { Geschlecht } from '@/types/Geschlecht';
import { InputValidationRule, InputValidationRules } from 'vuetify';
import {Terminkalender, Termin, Termindauer, Terminplatz} from '@/types/Terminkalender';
import { Verrechnungsart } from '@/types/Verrechnungsart';
import Dienstort from '@/types/Dienstort';
import Patientengruppe from '@/types/Patientengruppe';
import Studie from '@/types/Studie';
import Impfaktion from '@/types/Impfaktion';
import Diagnose from '@/types/Diagnose';
import ImpfSetting from '@/types/Impfsetting';

/* application.ts */

export interface ApplicationState {
    version: string;
    prevVersion: string;
    loading: boolean;
    maintenance: boolean;
    initialized: boolean;
    lastRoute: string;
}

/* arbeitsplaetze.ts */

export interface ArbeitsplaetzeState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        arbeitsplaetze: boolean;
        printers: boolean;
    };
    arbeitsplaetze: Arbeitsplatz[];
    printers: string[];
}

/* authentication.ts */

export type AclModul = 'ivs' | 'admin' | 'lager' | 'assertion' | 'system';
export type AclPermission = 'select' | 'index' | 'create' | 'update' | 'delete' | 'admin';

export interface LoginStatus {
    loggedIn: boolean;
    loggingIn: boolean;
}

export interface LoginData {
    username: string;
    password: string;
}

export interface AuthenticationState {
    status: LoginStatus;
    initialized: boolean;
    user: User | null;
    token: string | null;
    impfstellen: Impfstelle[];
    impfstelle_id: number;
    impfstelle: Impfstelle;
    dialog: {
        detail: boolean;
    };
    loaders: {
        detail: boolean;
        impfstelle: boolean;
    };
    arbeitsplaetze: Arbeitsplatz[];
    permissions: {
        [key in AclModul]: AclPermission[];
    };
}

/* briefarten.ts */

export interface BriefartenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        briefarten: boolean;
    };
    briefarten: Briefart[];
    platzhalter: string[];
}

/* cards.ts */

export interface CardsState {
    loaders: {
        cards: boolean;
    };
    cards: Card[];
}

/* elgauebertragung.ts */

export class ElgauebertragungEintrag extends Karteieintrag {
    public patient_nachname?: string | null;
    public patient_vorname?: string | null;
    public patient_titel?: string | null;
    public patient_titel_nach_name?: string | null;
    public patient_geburtsdatum?: string | null;

    constructor(data?: Partial<ElgauebertragungEintrag>) {
        super(data);
        Object.assign(this, data);
    }
}

export enum UebertragungStatus {
    NichtUebertragen = 0,
    WirdUebertragen = 1,
    WirdStorniert = 2,
    Uebertragen = 3,
}

export interface ElgaUebertagungFilter {
    zeitraum: [string, string];
    status: UebertragungStatus;
    patientStatus: string | null;
}

export interface ElgaUebertragungState {
    loaders: {
        impfungen: boolean;
        uebertragung: boolean;
        stornierung: boolean;
        suche: boolean;
        export: boolean;
        verfication: boolean;
    };
    items: ElgauebertragungEintrag[];
    selectedItems: ElgauebertragungEintrag[];
    patienten: ElgauebertragungEintrag[];
    page: number;
    prevFilter: ElgaUebertagungFilter;
    filter: ElgaUebertagungFilter;
}

/* emails.ts */

export interface EmailsFilter {
    zeitraum: string[];
}

export interface EmailsState {
    filter: EmailsFilter;
    loaders: {
        sending: boolean;
    };
}

/* globals.ts */

type JspmPrintRotation = unknown;
type JspmDuplexMode = unknown;

type PrintTarget = 'standarddrucker' | 'rezeptdrucker' | 'etikettendrucker';

export interface PrintPayload {
    data?: Blob | string;
    count?: number;
    printRotation?: JspmPrintRotation;
    paperName?: string;
    duplex?: JspmDuplexMode;
}

export interface ImpfPrintPayload extends PrintPayload {
    teilimpfungsKuerzel?: string;
}

export interface PrintData {
    printTarget: PrintTarget;
    payload: PrintPayload;
}

export interface ValidationRules {
    required: InputValidationRule;
    maxLength: (length: number) => InputValidationRule;
    maxNumberLength: (length: number) => InputValidationRule;
    onlyNumbers: InputValidationRule;
    onlyNumbersAllowNegative: InputValidationRule;
    numeric: (precision: number, scale: number) => InputValidationRule;
    isNumber: InputValidationRule;
    minLength: (length: number) => InputValidationRule;
    minNumberLength: (length: number) => InputValidationRule;
    exactDigits: (length: number) => InputValidationRule;
    validGender: (genders: Geschlecht[]) => InputValidationRule;
    validSvnr: InputValidationRule;
    validPrice: (value: string) => boolean | string;
    validDate: (date: string | null) => boolean | string;
    validTime: (time: string | null) => boolean | string;
    fromValidator(validator: Record<string, unknown>): InputValidationRules;
}

export interface GlobalsState {
    rules: ValidationRules;
    titelauswahl: Titel[];
    printers: {
        druckhinweis: boolean;
        arbeitsplatzhinweis: boolean;
        arbeitsplatzhinweisSource: string;
        data: PrintData | null;
        status: string;
    };
    formatDate: (input: string | null) => string | null;
    parseDate: (input: string | null) => string | null;
    formatTime: (input: string | null) => string | null;
    parseTime: (input: string | null) => string | null;
    sortDate: CompareFunction;
}

/* grids.ts */

export interface GridsState {
    saved: { [key: string]: GridParams<unknown> };
}

/* impfdb.ts */

export interface ImpfDbState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        impfstoffe: boolean;
    };
    impfstoffe: ImpfDbImpfstoff[];
}

/* impfempfehlungen.ts */

export interface ImpfempfehlungenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        impfempfehlungen: boolean;
    };
    impfempfehlungen: Impfempfehlung[];
}

/* impferinnerungen.ts */

export interface ImpferinnerungenPagination {
    page: number;
    itemsPerPage: number;
    pageStart: number;
    pageStop: number;
    pageCount: number;
    itemsLength: number;
}

export type ImpferinnerungenFilter = {
    faelligBis: string;
    email: string;
    sms: string;
    pagination: ImpferinnerungenPagination;
};

export type ZahlungserinnerungFilter = {
    email: string;
    sms: string;
};

export interface ImpferinnerungenState {
    dialogs: {
        kartei: boolean;
        brief: boolean;
    };
    loaders: {
        kartei: {
            loadingId: number | null;
            impfungen: boolean;
        };
        brief: {
            loadingId: number | null;
            print: boolean;
            export: boolean;
        };
        print: boolean;
        email: boolean;
        sms: boolean;
        mahnung: boolean;
        impferinnerungen: boolean;
    };
    impferinnerungen: Impferinnerung[];
    totalCount: number;
    selectedItems: Impferinnerung[];
    filter: ImpferinnerungenFilter;
    kartei: {
        patient: Patient;
        impfungen: Karteieintrag[];
        filter: {
            satzarten: number[];
        };
    };
    brief: Impfung | null;
}

export interface ZahlungserinnerungState {
    dialogs: {
        brief: boolean;
    };
    loaders: {
        brief: {
            loadingId: number | null;
            print: boolean;
            export: boolean;
        };
        print: boolean;
        email: boolean;
        sms: boolean;
        mahnung: boolean;
        offeneBelege: boolean;
    };
    belege: Beleg[];
    totalCount: number;
    selectedItems: Beleg[];
    filter: ZahlungserinnerungFilter;
    brief: Beleg | null;
    offeneZahlscheinBelege: Beleg[],
}

/* impferinnerungstexte.ts */

export type Impferinnerungstextart = 'Allgemein' | 'Auffrischung' | 'Teilimpfung';

export interface ImpferinnerungstexteState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        impferinnerungstexte: boolean;
        arten: boolean;
    };
    impferinnerungstexte: Impferinnerungstext[];
    platzhalter: string[];
    arten: Impferinnerungstextart[];
}

/* impfstoff.ts */

export interface ImpfstoffState {
    impfstoffe: Impfstoff[];
    teilimpfungen: Teilimpfungen[];
    specialSituationIndication: SpecialSituationIndication[];
    impfschema: Impfschema[];
    chargen: Charge[];
    elgaimpfstoffe: Elgaimpfstoffe[];
    elgadosis: ElgaDosis[];
    impfdosen: Impfdosis[];
    immunizationtarget: ImmunizationTarget[];
    selectedIS: Impfstoff | null;
    selectedCharge: Charge | null;
    selectedImpfschema: Impfschema | null;
    loaders: {
        impfstoffe: boolean;
    };
}

/* impfstoffe.ts */

export interface ImpfstoffeLoaders {
    save: boolean;
    delete: boolean;
    impfstoffe: boolean;
    export: boolean;
    impfabstaende: boolean;
}

export interface ImpfstoffeState {
    dialogs: {
        edit: boolean;
    };
    loaders: ImpfstoffeLoaders;
    impfstoffe: Impfstoff[];
}

/* diagnosen.ts */

export interface DiagnosenLoaders {
    save: boolean;
    delete: boolean;
    diagnosen: boolean;
    export: boolean;
}

export interface DiagnosenState {
    dialogs: {
        edit: boolean;
    };
    loaders: DiagnosenLoaders;
    diagnosen: Diagnose[];
}

/* kassafuehrung.ts */

export interface Kassaliste {
    abschlussnr: number | null;
    belege: Beleg[];
    leistungen: Belegposition[];
    datum: string | null;
    anfangssaldo: number | null;
}

export interface KassafuehrungState {
    dialogs: {
        kassaliste: boolean;
        kassaabschluss: boolean;
        zahlungseingang: boolean;
        stornieren: boolean;
    };
    loaders: {
        kassaliste: boolean;
        offeneBelege: boolean;
        kassaabschluss: boolean;
        zahlungseingang: boolean;
        kassaabschluesse: boolean;
        kassaabschlussPrint: boolean;
        kassaabschlussExport: boolean;
    };
    offeneBelege: Beleg[];
    offeneZahlscheinBelege: Beleg[];
    offeneBelegeLeistungen: Belegposition[];
    kassaabschluesse: Kassaabschluss[];
    kassaliste: Kassaliste;
    beleg: Beleg;
}

/* laender.ts */

export interface LaenderState {
    laender: Land[];
    postleitzahlen: Postleitzahl[];
}

/* lagerfuehrung.ts */

export interface Lagerliste {
    impfstoffe_bezeichnung: string | null;
    impfstoffe_verkaufspreis: number | null;
    impfstoffe_einkaufspreis: number | null;
    impfstoffe_preis_mitarbeiter: number | null;
    ablaufdatum: string | null;
    lagerbestand: number | null;
}

export interface LagerlisteJeCharge {
    bezeichnung: string;
    summe: number;
    mindestbestand: number;
    anmerkung: string;
    chargen: Charge[];
}

export interface LagerfuehrungState {
    dialogs: {
        edit: boolean;
        buchung: boolean;
        buchungsprotokoll: boolean;
        lagerliste: boolean;
        lagerlisteJeCharge: boolean;
    };
    loaders: {
        impfstoffliste: boolean;
        chargen: boolean;
        buchung: boolean;
        charge: boolean;
        buchungsprotokoll: boolean;
        buchungsprotokollExport: boolean;
        lagerliste: {
            loading: boolean;
            print: boolean;
            export: boolean;
        };
        lagerlisteJeCharge: {
            loading: boolean;
            print: boolean;
            export: boolean;
        };
        lagerlisteGesamt: boolean;
    };
    impfstoffliste: Impfstoff[];
    impfstoffchargen: Charge[];
    //old
    selected_impfstoff: Impfstoff | null;
    impfstoffe: Impfstoff[];
    chargen: Charge[];
    lagerbuchungsarten: Lagerbuchungsart[];
    lagerbuchungen: Lagerbuchung[];
    lagerliste: Lagerliste[];
    lagerlisteJeCharge: LagerlisteJeCharge[];
}

/* leistungen.ts */

export interface LeistungenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        leistungen: boolean;
    };
    leistungen: Leistung[];
}

/* logbuch.ts */

export interface LogbuchFilter {
    zeitraum: string[];
    loglevel: Priority;
    patnr: string | null;
    aktion: Methods | null;
    function: Eintragsart | null;
    user_id: number | null;
}

export interface LogbuchState {
    filter: LogbuchFilter | null;
}

/* logs.ts */

export interface LogsFilter {
    zeitraum: string[];
    loglevel: Priority[];
}

export interface LogsState {
    filter: LogsFilter;
}

/* patient.ts */

export interface PatientLoaders {
    impfung?: boolean;
    reiseplan?: boolean;
    patient?: boolean;
    patientuebertragung?: boolean;
    patientloeschung?: boolean;
    reiseplaene?: boolean;
    reiseplandelete?: boolean;
}

export interface ElgaLoaders {
    patient?: boolean;
    save?: boolean;
    uebertragung?: boolean;
    stornierung?: boolean;
    search?: boolean;
}

export interface BelegLoaders {
    save?: boolean;
    storno?: boolean;
    print?: boolean;
    export?: boolean;
    abschluss?: boolean;
}

export interface BriefLoaders {
    save?: boolean;
    saveAndPrint?: boolean;
    print?: boolean;
    export?: boolean;
    delete?: boolean;
}

export interface UntersuchungLoaders {
    save?: boolean;
    print?: boolean;
    printBefund?: boolean;
    printBefunde?: boolean;
    delete?: boolean;
    email?: boolean;
}

export interface PatientState {
    dialogs: {
        patientDetail: boolean;
        patientNew: boolean;
        impfungNew: boolean;
        reiseplan: boolean;
        rechnung: boolean;
        druckok: boolean;
        addAgainToWartelisteDialog: boolean;
        druckansicht: boolean;
        texteintrag: boolean;
        rezept: boolean;
        leistung: boolean;
        diagnose: boolean;
        anamnese: boolean;
        anamneselistitem: boolean;
        brief: boolean;
        impfungEdit: boolean;
        impfungNachtrag: boolean;
        zahlungart: boolean;
        druckhinweis: boolean;
        patientDruckDetail: boolean;
        patientEtikettenDetail: boolean;
        elgaPatient: boolean;
        elgaSuche: boolean;
        patientuebertragung: boolean;
        ssnoSearch: boolean;
        ecard: boolean;
        ecardInfo: boolean;
        dekurs: boolean;
        untersuchung: boolean;
        warteliste: boolean;
        zurKassa: boolean;
        sammelrechnung: boolean;
    };
    loaders: {
        impfung: boolean;
        impfungundneu: boolean;
        impfungImpferinnerung: boolean;
        impfungErinnerungGesendetReset: boolean;
        impfungAbrechnung: boolean;
        untersuchungAbrechnung: boolean;
        erinnerungen: boolean;
        untersuchungen: boolean;
        reiseplan: boolean;
        patient: boolean;
        patientuebertragung: boolean;
        patientloeschung: boolean;
        reiseplaene: boolean;
        reiseplandelete: boolean;
        reiseplanprint: boolean;
        distinctBefundwerttexte: boolean;
        text: {
            save: boolean;
            delete: boolean;
            download: boolean;
            downloads: number[];
        };
        rezepteintrag: {
            save: boolean;
            print: boolean;
            export: boolean;
            delete: boolean;
        };
        leistung: {
            save: boolean;
            delete: boolean;
        };
        diagnose: {
            save: boolean;
            delete: boolean;
        };
        anamnese: {
            save: boolean;
            delete: boolean;
            print: boolean;
        };
        brief: {
            save: boolean;
            saveAndPrint: boolean;
            print: boolean;
            export: boolean;
            delete: boolean;
        };
        untersuchung: {
            save: boolean;
            printBefund: boolean;
            printBefunde: boolean;
            printUntersuchung: boolean;
            printUntersuchungen: boolean;
            delete: boolean;
            email: boolean;
            einsendeformular: boolean;
            untersuchungsanforderung: boolean;
        };
        warteliste: {
            save: boolean;
        };
        beleg: {
            save: boolean;
            storno: boolean;
            print: boolean;
            export: boolean;
            exportXml: boolean;
            abschluss: boolean;
            berichtigung: boolean;
            mahnung: boolean;
        };
        druck: boolean;
        delete: boolean;
        etiketten: boolean;
        export: boolean;
        exportPdf: boolean;
        folgetermin: boolean;
        elga: {
            patient: boolean;
            save: boolean;
            uebertragung: boolean;
            save_ohne_adresse: boolean;
            stornierung: boolean;
            search: boolean;
        };
        ecard: {
            info: boolean;
        };
        ecardInfo: {
            info: boolean;
        };
        dekurs: {
            save: boolean;
        };
    };
    patientMitAdresse: boolean;
    showAllePatientInfo: boolean;
    patientMitAdresseUndPlan: boolean;
    patientData: Patient | null;
    loadedPatientData: Patient | null;
    elgaPatient: ElgaPatient;
    elgaPatienten: ElgaPatient[];
    elgaPatientAnlage: ElgaPatient;
    tempPatientData: Patient;
    patientDataNew: Patient;
    selected_text: Karteieintrag;
    selected_rezepteintrag: Karteieintrag;
    selected_leistungseintrag: Karteieintrag;
    selected_brief: Karteieintrag;
    selected_untersuchung: Karteieintrag;
    selected_diagnose: Karteieintrag;
    selected_anamnese: Karteieintrag;
    selected_offen: PlanItem<Planimpfung | PatUntersuchung>[];
    zahlendePatienten: Warteliste[];
    untersuchungen: PatUntersuchung[];
    patienten: Patient[];
    distinctBefundwerttexte: string[];
    cardInfo: CardInfo | null;
    cardInfoPatienten: Patient[];
    patientLastAnamnese: Impfung;
}

/* patientenauswertung.ts */

export interface PatientenauswertungState {
    dialogs: {
        filter: boolean;
    };
    loaders: {
        patienten: boolean;
        kartei: {
            loadingId: number | null;
            impfungen: boolean;
        };
    };
    patienten: Patient[];
    kartei: {
        patient: Patient;
        impfungen: Karteieintrag[];
        filter: {
            satzarten: number[];
        };
    };
}

/* patientimpfungen.ts */

export interface PatImpfungenFilter {
    /* @see Satzart */
    satzarten: number[];
    /* Eigene übertragene Impfungen welche von der ELGA kommen */
    elga: boolean;
    gruppierung: boolean;
}

export interface PatientimpfungenState {
    patientImpfungNew: Impfung;
    impfungen: Karteieintrag[];
    erinnerungen: Impferinnerung[];
    filter: PatImpfungenFilter;
    dt_load: boolean;
    loadingText: boolean;
    selected_impfeintrag: Karteieintrag;
    gruppiert: boolean;
}

/* patientrechnungen.ts */

export interface PatientrechnungenState {
    druckvorgang: boolean;
    druckvorgangRuecksetzung: boolean;
    abbrechungsvorgang: boolean;
    abbrechungsvorgangAlle: boolean;
    loadRechnung: boolean;
    beleg: Beleg;
    belegPositionen: Belegposition[];
    belegPatienten: Patient[];
    rechnungen: Beleg[];
    rechnungenpositionen: Belegposition[];
    rechnungenpatienten: BelegPatient[];
    selected_rechnung: Beleg;
}

/* dienstorte.ts */

export interface DienstorteState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        dienstorte: boolean;
        printers: boolean;
    };
    dienstorte: Dienstort[];
    printers: string[];
}

/* impfaktionen.ts */

export interface ImpfaktionenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        impfaktionen: boolean;
        printers: boolean;
    };
    impfaktionen: Impfaktion[];
    printers: string[];
}

/* studien.ts */

export interface StudienState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        studien: boolean;
        printers: boolean;
    };
    studien: Studie[];
    printers: string[];
}

/* patientengruppen.ts */

export interface PatientengruppenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        gruppen: boolean;
        printers: boolean;
    };
    gruppen: Patientengruppe[];
}

/* patientreiseplaene.ts */

export interface PatientReiseplaeneState {
    reiseplaene: Reiseplan[];
    reiseplanimpfungen: Planimpfung[];
    reiseplanuntersuchungen: PatUntersuchung[];
    selected_reiseplan: Reiseplan;
    selected_offeneImpfung: Planimpfung | null;
    aktuelleAnzeigen: string | null;
}

export type ExtendedPatientState =
    PatientState &
    PatientimpfungenState &
    PatientrechnungenState &
    PatientReiseplaeneState;

/* personen.ts */

export interface PersonenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        personen: boolean;
    };
    personen: Person[];
}

/* impfsettings.ts */

export interface ImpfsettingsState {
    loaders: {
        impfsettings: boolean;
    };
    impfsettings: ImpfSetting[];
}

/* reise.ts */

export interface ReiseState {
    reiseziele: Reiseziel[];
    reisearten: Reiseart[];
    loaders: {
        reiseziele: boolean;
        reisearten: boolean;
    };
}

/* reisearten.ts */

export interface ReiseartenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        reisearten: boolean;
    };
    reisearten: Reiseart[];
}

/* reiseziele.ts */

export interface ReisezieleState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        reiseziele: boolean;
    };
    reiseziele: Reiseziel[];
}

/* anforderungen.ts */

export interface AnforderungenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        anforderungen: boolean;
    };
    anforderungen: Anforderung[];
    dauer: Termindauer[];
}

/* rezepte.ts */

export interface RezepteState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        rezepte: boolean;
    };
    rezepte: Rezept[];
}

/* session.ts */

export interface LastImpfung {
    impfstoff_id: number | null;
    impfstoffcharge_id: number | null;
    impfschema_code: string | null;
    anzahl: number | null;
    teilimpfungsnr: number | null;
    naechsteteilimpfungsnr: number | null;
    impfdatum: string | null;
    naechstesimpfdatum: string | null;
}

export interface SessionState {
    arzt: number | null;
    assistenz: number | null;
    arbeitsplatz: number | null;
    lastImpfung: LastImpfung | null;
    lastNachtrag: LastImpfung | null;
    impfstelle: number | null;
    abrechnung: Verrechnungsart | null;
    abrechnungLeistung: Verrechnungsart | null;
    verrechnungUntersuchung: string | null;
    warteliste: boolean;
}

/* statistik.ts */

export interface StatistikState {
    verrechnungen: OffeneVerrechnung[];
    wavmLogs: ImpfDbLog[];
    dialog: {
        impfung: boolean;
        umsatz: boolean;
        verrechnung: boolean;
        rechnung: boolean;
        salden: boolean;
        reise: boolean;
        patienten: boolean;
        stipas: boolean;
        wavm: boolean;
        tagesjournal: boolean;
        jahrgang: boolean;
        patientenmitimpfung: boolean,
        patientenmitleistungen: boolean,
        patientenstatistik: boolean,
        patientenstatistik_detailiert: boolean,
        preislisten: boolean,
    };
    loaders: {
        impfung: boolean;
        patienten: boolean;
        verrechnung: boolean;
        jahrgang: boolean;
        umsatz: {
            print: boolean;
            export: boolean;
        };
        rechnung: {
            print: boolean;
            export: boolean;
            exportFibu: boolean;
        };
        salden: {
            print: boolean;
        };
        patientenstatistik: {
            print: boolean;
            export: boolean;
        };
        reise: {
            print: boolean;
            export: boolean;
        };
        stipas: {
            print: boolean;
            export: boolean;
        };
        wavm: {
            export: boolean;
            preview: boolean;
            repeat: boolean;
            logs: boolean;
        };
        tagesjournal: {
            print: boolean;
            export: boolean;
        };
        patientenmitleistungen: {
            export: boolean;
      };
        patientenmitimpfungen: {
            export: boolean;
      };
        impfauswertung: {
            print: boolean;
      };
        patientenstatistik_detailiert: {
            print: boolean;
            kontrolle?: boolean;
      };
        patientenstatistik_studenten: {
            print: boolean;
            kontrolle?: boolean;
      };
        preislisten: {
            print: boolean;
            kontrolle?: boolean;
      };
    };
}

/* systemdaten.ts */

export interface SystemdatenState {
    systemdaten: Systemdaten;
    dialog: {
        detail: boolean;
    };
    loaders: {
        systemdaten: boolean;
        save: boolean;
    };
}

/* terminarten.ts */

export interface TerminartenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        terminarten: boolean;
    };
    terminarten: Terminart[];
}

/* terminarten.ts */

export interface TerminplaetzeState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        terminplaetze: boolean;
    };
    terminplaetze: Terminplatz[];
}

/* terminkalender.ts */

export interface TerminSucheFilter {
    von: string;
    bis: string;
    zeit: string;
    dauer: string;
    platznr: string;
    terminart: string;
    erschienen: string;
    patient: number | string;
    hinweis: string;
}

export interface TerminkalenderState {
    day: string;
    type: string;
    termin: Termin;
    kalender: Terminkalender;
    anmeldungen: Termin[];
    dialog: boolean;
    anmeldungDialog: boolean;
    searchDialog: boolean;
    loaders: {
        save: boolean;
        delete: boolean;
        deleteterminserie: boolean;
        termine: boolean;
        suche: boolean;
        anmeldungen: boolean;
        sync: boolean;
    };
    filter: {
        von: string;
        bis: string;
    } | null;
    filterAnmeldung: {
        zeitraum: [
        von: string,
        bis: string,
        ],
        zeitraum_gebucht: [
            von: string,
            bis: string,
        ],
        status: string
        name: string
    };
    suche: {
        termine: Termin[];
        filter: TerminSucheFilter;
    };
}

/* texte.ts */

export interface TexteState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        texte: boolean;
    };
    texte: Text[];
}

/* titel.ts */

export interface TitelState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        titel: boolean;
    };
    titel: Titel[];
}

/* untersuchungen.ts */

export interface UntersuchungenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        untersuchungen: boolean;
    };
    untersuchungen: Untersuchung[];
    befundvarianten: Untersuchungsbefundvariante[];
}

/* users.ts */

export interface UserValidations {
    username: {
        min: number;
        max: number;
    };
    password: {
        min: number;
        max: number;
    };
}

export interface UsersState {
    dialogs: {
        edit: boolean;
        selfEdit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        users: boolean;
    };
    users: User[];
    roles: UserRoleType[];
    validations: UserValidations;
}

/* versicherungen.ts */

export interface VersicherungenState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        save: boolean;
        delete: boolean;
        versicherungen: boolean;
    };
    versicherungen: Versicherung[];
}

/* warteliste.ts */

export interface WartelisteFilter {
    fertig: boolean | null;
    wartet?: boolean | null;
    arbeitsplatz?: boolean | null;
}

export interface WartelisteState {
    dialogs: {
        edit: boolean;
    };
    loaders: {
        finish: boolean;
        delete: boolean;
        warteliste: boolean;
        update: boolean;
        kassa: boolean;
    };
    warteliste: Warteliste[];
    filter: WartelisteFilter;
    refresh: Interval | null;
    wartelisteItem: Warteliste
}

/* zahlungsarten.ts */

export interface ZahlungsartenState {
    arten: Zahlungsart[];
    loaders: {
        arten: boolean;
    };
}

export interface RootState {
    application: ApplicationState;
    arbeitsplaetze: ArbeitsplaetzeState;
    authentication: AuthenticationState;
    briefarten: BriefartenState;
    cards: CardsState;
    elgauebertragung: ElgaUebertragungState;
    globals: GlobalsState;
    grids: GridsState;
    impfdb: ImpfDbState;
    impfempfehlungen: ImpfempfehlungenState;
    impferinnerungen: ImpferinnerungenState;
    impferinnerungstexte: ImpferinnerungstexteState;
    impfstoff: ImpfstoffState;
    impfstoffe: ImpfstoffeState;
    impfsettings: ImpfsettingsState;
    kassafuehrung: KassafuehrungState;
    laender: LaenderState;
    lagerfuehrung: LagerfuehrungState;
    leistungen: LeistungenState;
    logbuch: LogbuchState;
    logs: LogsState;
    patient: ExtendedPatientState;
    patientenauswertung: PatientenauswertungState;
    personen: PersonenState;
    reise: ReiseState;
    reisearten: ReiseartenState;
    reiseziele: ReisezieleState;
    rezepte: RezepteState;
    session: SessionState;
    statistik: StatistikState;
    systemdaten: SystemdatenState;
    terminkalender: TerminkalenderState;
    texte: TexteState;
    titel: TitelState;
    untersuchungen: UntersuchungenState;
    users: UsersState;
    versicherungen: VersicherungenState;
    warteliste: WartelisteState;
    zahlungsarten: ZahlungsartenState;
    dienstorte: DienstorteState;
}
